import { useState, useRef, useEffect } from "react";
import { Globe, ExternalLink, Check } from "lucide-react";
import { SUPPORTED_LOCALES, googleTranslateUrl, useTranslation } from "@/i18n";

/* Language picker — a small globe button that opens a native-labelled menu.
   Also exposes the "Open in Google Translate" fallback for the 100+ languages
   we don't support natively. Mobile-first: ~44px tap target. */
export const LanguageSwitcher = ({ compact = false }) => {
  const { t, locale, setLocale } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const current = SUPPORTED_LOCALES.find((l) => l.code === locale) || SUPPORTED_LOCALES[0];

  return (
    <div className="relative inline-block" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost text-xs inline-flex items-center gap-1.5"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("common.languageLabel", "Language")}
        data-testid="language-switcher-trigger"
      >
        <Globe size={14} />
        <span translate="no">{compact ? current.code.toUpperCase() : current.native}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 bg-[#0a0908] border border-[#2d2424] rounded-md shadow-xl z-50 overflow-hidden"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => { setLocale(l.code); setOpen(false); }}
              role="menuitem"
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-[#1a1413] ${
                l.code === locale ? "text-[#d4af37]" : "text-[#f4f0ec]"
              }`}
              data-testid={`language-option-${l.code}`}
              lang={l.code}
            >
              <span translate="no">{l.native}</span>
              {l.code === locale && <Check size={14} />}
            </button>
          ))}
          <div className="border-t border-[#2d2424] px-3 py-2 text-[11px] text-[#a39797]">
            {t("common.translateOtherLanguages")}
            <a
              href={googleTranslateUrl()}
              target="_blank"
              rel="noreferrer"
              className="text-[#d4af37] inline-flex items-center gap-1 hover:underline"
              data-testid="google-translate-fallback"
            >
              {t("common.openInGoogleTranslate")}
              <ExternalLink size={10} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
